(() => {
  const MOBILE_MQ = '(max-width: 699px)';
  const DRAG_THRESHOLD = 90;
  const AXIS_LOCK = 10;

  const KIT_MODAL_CLOSE_FNS = {
    'curated-kit-modal': 'closeCuratedKitModal',
    'style-diff-modal': 'closeStyleDiffModal',
    'product-modal': 'closeProductModal'
  };

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function isScrollable(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.scrollHeight <= el.clientHeight + 2) return false;
    const overflowY = window.getComputedStyle(el).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  }

  /**
   * Find the element that actually scrolls under the finger. The touch may start inside a slotted
   * light-DOM node (size chart) or deep inside a modal body, so the composed path is walked from the
   * touch target outwards until the sheet itself is reached.
   */
  function findScroller(event, content) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const chain = path.length ? path : [event.target];

    for (const el of chain) {
      if (isScrollable(el)) return el;
      if (el === content) break;
    }

    return isScrollable(content) ? content : null;
  }

  function atTop(scroller) {
    return !scroller || scroller.scrollTop <= 1;
  }

  function setupSheetGestures({ root, content, closeFn, excludeSelector }) {
    if (!root || !content || root.dataset.waldruhGestures === '1') return;
    root.dataset.waldruhGestures = '1';

    let startY = 0;
    let startX = 0;
    let currentY = 0;
    let axis = null;
    let canDrag = false;
    let dragging = false;

    const resetTransform = () => {
      content.style.removeProperty('transition');
      content.style.removeProperty('transform');
    };

    const onStart = (event) => {
      canDrag = false;
      dragging = false;
      axis = null;
      if (!isMobile()) return;
      if (event.target.closest && event.target.closest(excludeSelector)) return;

      startX = event.changedTouches[0].screenX;
      startY = event.changedTouches[0].screenY;
      currentY = startY;
      // Only a pull down from the very top dismisses; swiping up always scrolls the content.
      canDrag = atTop(findScroller(event, content));
    };

    const onMove = (event) => {
      if (!canDrag) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.screenX - startX;
      const deltaY = touch.screenY - startY;

      if (!axis) {
        if (Math.abs(deltaX) < AXIS_LOCK && Math.abs(deltaY) < AXIS_LOCK) return;
        axis = Math.abs(deltaY) > Math.abs(deltaX) ? 'y' : 'x';
      }
      if (axis !== 'y' || deltaY <= 0) return;

      currentY = touch.screenY;
      dragging = true;
      window.kitProductModalDidSwipeUntil = Date.now() + 500;
      content.style.transition = 'none';
      content.style.transform = `translateY(${deltaY}px)`;
      event.preventDefault();
    };

    const onEnd = () => {
      if (!dragging) return;

      const deltaY = currentY - startY;
      dragging = false;
      axis = null;

      if (deltaY > DRAG_THRESHOLD) {
        resetTransform();
        closeFn();
        return;
      }

      content.style.transition = 'transform 0.2s ease';
      content.style.transform = 'translateY(0)';
      window.setTimeout(resetTransform, 220);
    };

    content.addEventListener('touchstart', onStart, { passive: true });
    content.addEventListener('touchmove', onMove, { passive: false });
    content.addEventListener('touchend', onEnd, { passive: true });
    content.addEventListener('touchcancel', onEnd, { passive: true });
  }

  /**
   * Close a kit modal without depending on the inline onclick handlers, which can be swallowed on
   * touch devices. The named close function is still called first so its bookkeeping (slide index,
   * content teardown) runs.
   */
  function hardCloseKitModal(modal) {
    if (!modal) return;

    modal.style.removeProperty('transition');
    modal.style.removeProperty('transform');

    const closeFnName = KIT_MODAL_CLOSE_FNS[modal.id];
    if (closeFnName && typeof window[closeFnName] === 'function') {
      try {
        window[closeFnName]();
      } catch (error) {
        /* fall through to the manual teardown below */
      }
    }

    modal.classList.remove('active');
    document.getElementById(`${modal.id}-overlay`)?.classList.remove('active');
    document.body.style.overflow = '';
  }

  function pointInRect(x, y, rect, pad) {
    const slack = pad || 0;
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      x >= rect.left - slack &&
      x <= rect.right + slack &&
      y >= rect.top - slack &&
      y <= rect.bottom + slack
    );
  }

  /**
   * Hit-test the close control by coordinates rather than by event target. Taps on the X were being
   * absorbed before they reached the button, so the geometry of the button (and of the empty part of
   * the header next to the title) is what decides.
   */
  function modalForClosePoint(x, y) {
    const modals = document.querySelectorAll('.kit-builder__modal.active');

    for (const modal of modals) {
      const button = modal.querySelector('.kit-builder__modal-close');
      if (button && pointInRect(x, y, button.getBoundingClientRect(), 10)) return modal;

      const header = modal.querySelector('.kit-builder__modal-header');
      if (!header || !pointInRect(x, y, header.getBoundingClientRect())) continue;

      const title = modal.querySelector('.kit-builder__modal-header-title');
      const titleRect = title?.getBoundingClientRect();
      if (!titleRect || titleRect.width === 0 || x > titleRect.right + 8) return modal;
    }

    return null;
  }

  function setupKitModalCloseDelegation() {
    let lastCloseAt = 0;

    const onCloseTap = (event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = path[0] && path[0].closest ? path[0] : event.target;

      let modal = null;

      if (target && target.closest) {
        const closeButton = target.closest('.kit-builder__modal-close');
        const overlay = target.closest('.kit-builder__modal-overlay');
        if (closeButton) modal = closeButton.closest('.kit-builder__modal');
        else if (overlay) modal = document.getElementById(overlay.id.replace(/-overlay$/, ''));
      }

      if (!modal) {
        const point = event.type === 'touchend' ? event.changedTouches?.[0] : event;
        if (!point || (point.clientX === 0 && point.clientY === 0)) return;
        modal = modalForClosePoint(point.clientX, point.clientY);
      }

      if (!modal || !modal.classList.contains('active')) return;

      if (event.type === 'touchend') event.preventDefault();
      event.stopPropagation();

      const now = Date.now();
      if (now - lastCloseAt < 500) return;
      lastCloseAt = now;

      hardCloseKitModal(modal);
    };

    document.addEventListener('touchend', onCloseTap, { capture: true, passive: false });
    document.addEventListener('click', onCloseTap, true);
  }

  /*
    Exposed so a modal that is created after load (the curated kit Product details popup builds its
    own container the first time it opens) can still get the pull-down-to-close gesture.
  */
  function setupKitModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    setupSheetGestures({
      root: modal,
      content: modal,
      closeFn: () => hardCloseKitModal(modal),
      excludeSelector:
        '.kit-builder__modal-close, .kit-builder__modal-slider-nav, .kit-builder__modal-accordion-header, .kit-builder__modal-swatch, button, a, input, select, textarea'
    });
  }

  function setupPrestigeModal(modal) {
    if (!modal || modal.dataset.waldruhGesturesBound === '1') return;
    modal.dataset.waldruhGesturesBound = '1';

    const bind = () => {
      const content = modal.shadowRoot?.querySelector('[part="content"]');
      if (!content) return;

      content.style.removeProperty('transition');
      content.style.removeProperty('transform');

      setupSheetGestures({
        root: modal,
        content,
        closeFn: () => modal.hide?.(),
        excludeSelector:
          'button, a, input, select, textarea, .sc-tab, [part="close-button"], dialog-close-button'
      });
    };

    ['dialog:before-show', 'dialog:after-show'].forEach((type) => modal.addEventListener(type, bind));
    bind();
  }

  /**
   * Keep a newly opened accordion in view: on small screens the drawer content sits below the
   * image, so opening "Fit" or "Fabric" can reveal content off-screen.
   */
  function setupAccordionReveal() {
    document.addEventListener('click', (event) => {
      if (!isMobile()) return;
      const header = event.target.closest?.(
        '#product-modal .kit-builder__modal-accordion-header, #curated-kit-modal .kit-builder__modal-accordion-header'
      );
      if (!header) return;

      const accordion = header.closest('.kit-builder__modal-accordion');
      window.setTimeout(() => {
        if (accordion?.classList.contains('open')) {
          accordion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 620);
    });
  }

  window.waldruhSetupKitModal = setupKitModal;

  function init() {
    setupKitModalCloseDelegation();
    ['curated-kit-modal', 'style-diff-modal', 'product-modal'].forEach(setupKitModal);

    document.querySelectorAll('quick-buy-modal, #waldruh-size-chart-modal').forEach(setupPrestigeModal);
    document.addEventListener('dialog:after-show', (event) => {
      if (event.target?.matches?.('quick-buy-modal, #waldruh-size-chart-modal')) {
        setupPrestigeModal(event.target);
      }
    });

    setupAccordionReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
